import clsx from 'clsx';
import { ToastManagerContext } from 'components/ToastManager';
import { StripeEnv } from 'hooks/internalApi';
import { useContextSafely } from 'hooks/useContextSafely';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FragmentEnv } from 'utils/env';
import { putLocalStorage } from 'utils/localStorage';
import { getSessionStorage } from 'utils/sessionStorage';
import { v4 } from 'uuid';
import { useLocation } from 'wouter';
import { useWorkspaceId } from 'wrappers/WorkspaceProvider';

import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';
import { LinkIcon } from '@fragment/ui/src/components/Icon/LinkIcon/LinkIcon';
import { InlineError } from '@fragment/ui/src/components/InlineError/InlineError';
import { TextInput } from '@fragment/ui/src/components/Input/TextInput';
import { Toast } from '@fragment/ui/src/components/Toast';
import { Toggle } from '@fragment/ui/src/components/Toggle/Toggle';

import { DocsLink } from './DocsLink';

type FormData = {
  name: string;
  env: StripeEnv;
};

export const OnboardStripeLinkForm = () => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { isValid, errors },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      env: StripeEnv.Testmode,
    },
  });
  const workspaceId = useWorkspaceId();
  const watchEnv = watch('env', StripeEnv.Testmode);

  const authorizeWithStripe = (data: FormData) => {
    const { name, env } = data;
    const state = v4();
    const clientId =
      data.env === StripeEnv.Livemode
        ? FragmentEnv.stripe.clientId.livemode
        : FragmentEnv.stripe.clientId.testmode;
    // Every redirectURI must be registered in the Stripe dashboard
    const redirectUri = `${window.location.origin}/oauth/stripe/redirect`;
    // TODO: Get permission to change scope to `read_only`
    const stripeRedirect = `https://connect.stripe.com/oauth/authorize?response_type=code&client_id=${clientId}&scope=read_write&state=${state}&redirect_uri=${redirectUri}`;
    putLocalStorage('stripe_onboard_payload', {
      name,
      env,
      state,
      workspaceId,
    });
    window.location.href = stripeRedirect;
  };

  const { showToast } = useContextSafely(ToastManagerContext);
  const [location, unusedSetLocation] = useLocation();
  useEffect(() => {
    const onboardError = getSessionStorage('onboard_stripe_error_message');
    if (onboardError) {
      showToast(<Toast message={onboardError} type="error" />);
    }
  }, [showToast]);

  return (
    <form
      // eslint-disable-next-line @typescript-eslint/no-misused-promises -- grandfathered
      onSubmit={handleSubmit((data) => {
        authorizeWithStripe(data);
      })}
    >
      <div className={clsx('space-y-f2')}>
        <div className={clsx('flex flex-row justify-between')}>
          <div className="flex items-center space-x-[2px]">
            <LinkIcon type="StripeLink" />
            <span>Stripe</span>
          </div>
          <DocsLink linkType="StripeLink" />
        </div>
        <div className={clsx('space-y-f2')}>
          <TextInput
            label="Name"
            width="w-full"
            flexContainer="space-y-0"
            size="md"
            padding="p-f0"
            // To keep backwards compatibility with the existing behavior
            placeholder={
              watchEnv === StripeEnv.Livemode ? 'Stripe' : 'Stripe test mode'
            }
            {...register('name', {
              required: {
                value: true,
                message: 'Enter a name for your Stripe integration',
              },
              maxLength: {
                value: 128,
                message:
                  'Integration names should be shorter than 128 characters',
              },
            })}
          />
          {errors.name?.message && (
            <InlineError singleLine>{errors.name.message}</InlineError>
          )}
          <Toggle
            onLeftClick={() => setValue('env', StripeEnv.Testmode)}
            onRightClick={() => setValue('env', StripeEnv.Livemode)}
            buttonHeight="h-f2"
            buttonPadding="px-f0"
            leftLabel="Test Mode"
            rightLabel="Live Mode"
            initialActiveButton="left"
          />

          <PrimaryButtonV2 disabled={!isValid} type="submit">
            Add Link
          </PrimaryButtonV2>
        </div>
      </div>
    </form>
  );
};
